import * as React from 'react';
import { useDraggable } from '@dnd-kit/core';
import { SEMCOR } from '@/lib/semaforo';
import {
  Check,
  ChevronLeft,
  ChevronRight,
  CircleHelp,
  Forklift,
  Hand,
  MoveVertical,
  type LucideIcon,
} from 'lucide-react';
import { urlImagemProduto } from '@wms/shared';
import { useMapa3D } from '@/hooks/consultas';
import { cn, numero } from '@/lib/utils';
import { Ramo } from '@/componentes/mapa/Ramo';
import type { Mapa3DResposta, VagaMapa } from '@/lib/tipos';
import { EstadoErro } from '@/componentes/ui/estados';
import { Esqueleto } from '@/componentes/ui/primitivos';

/**
 * O desenho de uma rua, arrastável — a peça que as telas de movimentação
 * compartilham: compensar negativo e transferir livremente.
 *
 * Ele não sabe por que a transferência está sendo feita. Recebe quais vagas
 * têm saldo do SKU (as que podem ser SAÍDA), qual é a origem, qual é o
 * destino e, opcionalmente, uma posição em destaque especial — o negativo,
 * quando a tela veio de lá.
 *
 * Duas decisões que vieram de bugs reais e não devem ser desfeitas:
 *
 *   - **Toda vaga é destino, sem um `useDroppable` por célula.** Seriam ~1.300
 *     numa rua, todos medidos pelo dnd-kit a cada arraste. Aqui o desenho é
 *     uma zona só: durante o arraste um `pointermove` no documento pergunta ao
 *     `elementFromPoint` qual `[data-loc]` está sob o cursor.
 *   - **A rolagem inicial é efeito, não `ref` inline.** O React refaz callback
 *     de ref a cada render, e o arraste re-renderiza a cada movimento do
 *     mouse: a rua voltava sozinha ao começo no meio do gesto.
 */

/*
 * As cores vinham copiadas do mapa, com um comentario dizendo "mesmas cores".
 * Era verdade por acaso -- nada obrigava. Agora sao as mesmas de fato; a
 * reexportacao existe para nao quebrar quem ja importava daqui.
 */
export { SEMCOR };

export const LARGURA_LOTE = 34;
export const ALTURA_VAGA = 30;
const LARGURA_ANDAR = 58;
const LARGURA_BOX = 46;

/**
 * Ids do dnd-kit.
 *
 * A MESMA posição aparece em dois lugares arrastáveis: o chip da tira e a
 * vaga dentro do desenho. Precisam de ids DIFERENTES — o dnd-kit indexa
 * draggables por id, e dois com o mesmo nome se sobrescrevem: o arraste
 * parecia começar e o solte não fazia nada. Os dois resolvem para o mesmo
 * localizador em `locDoId`.
 */
export const idVaga = (localizador: number): string => `loc:${localizador}`;
export const idChip = (localizador: number): string => `chip:${localizador}`;
export const locDoId = (id: string): number | null => {
  const m = /^(?:loc|chip):(\d+)$/.exec(id);
  return m ? Number(m[1]) : null;
};

export interface Geo {
  modulo: string;
  corredor: string;
  box: string;
  /** null = BOX fora do padrão conhecido; a tela não afirma altura. */
  andar: number | null;
}

/**
 * Endereço sempre com espaço, nunca com hífen.
 *
 * O mapa monta 'modulo-corredor-box' e outras consultas montam com espaço;
 * ver os dois formatos na MESMA tela faz parecer que são coisas diferentes.
 */
export function comoEndereco(endereco: string): string {
  return endereco.trim().split(/[\s\-/]+/).filter(Boolean).join(' ');
}

/**
 * Lê a geometria do endereço ('037-MC003-301', '037 MC003 301', '037/MC003/301').
 *
 * Mesma regra do servidor (`mapper.derivarGeometria`): no BOX `NNP` o primeiro
 * dígito é o andar. Repetida aqui porque as telas precisam do andar de
 * posições que chegam por listas, sem passar pelo desenho da rua.
 */
export function lerEndereco(endereco: string): Geo {
  const [modulo = '', corredor = '', box = ''] = endereco.trim().split(/[\s\-/]+/);
  let andar: number | null = null;
  if (box === '0' || box === '1' || box === '001') andar = 0;
  else if (/^[1-9]\d{1,2}$/.test(box)) andar = Number(box[0]);
  return { modulo, corredor, box, andar };
}

/**
 * Como o operador alcança a vaga. É o dado que decide a tarefa no chão:
 * mandar alguém a pé até um andar 6 sem empilhadeira é perder a viagem.
 *
 * Informativo, nunca impeditivo — altura de vaga não é regra de negócio.
 */
export function comoAlcancar(andar: number | null): { texto: string; Icone: LucideIcon } {
  if (andar === null) return { texto: 'altura não identificada', Icone: CircleHelp };
  if (andar === 0) return { texto: 'no piso · retirada na mão', Icone: Hand };
  if (andar <= 2) return { texto: `andar ${andar} · paleteira ou escada`, Icone: MoveVertical };
  return { texto: `andar ${andar} · usar empilhadeira`, Icone: Forklift };
}

export const DICA_ALCANCE = 'Informação de logística — não impede a transferência.';

export function Legenda({
  cor,
  rotulo,
  contorno = false,
}: {
  cor: string;
  rotulo: string;
  contorno?: boolean;
}): JSX.Element {
  return (
    <span className="inline-flex items-center gap-1.5">
      <i
        className="h-3 w-3 rounded-[3px]"
        style={
          contorno
            ? { boxShadow: 'inset 0 0 0 1px rgb(var(--cor-borda-forte))' }
            : { background: cor }
        }
      />
      {rotulo}
    </span>
  );
}

/** A legenda completa do desenho, igual nas duas telas. */
export function LegendaRack(): JSX.Element {
  return (
    <span className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[10.5px] text-textoFraco">
      <Legenda cor={SEMCOR.vazio ?? ''} rotulo="livre" />
      <Legenda cor={SEMCOR.baixo ?? ''} rotulo="até 40%" />
      <Legenda cor={SEMCOR.medio ?? ''} rotulo="40–75%" />
      <Legenda cor={SEMCOR.alto ?? ''} rotulo="+75%" />
      <Legenda cor={SEMCOR.sem_medida ?? ''} rotulo="sem medida" />
      <span className="inline-flex items-center gap-1.5 font-semibold text-primaria">
        <i className="h-3 w-3 rounded-[3px] bg-superficie2 ring-2 ring-inset ring-primaria" />
        tem saldo deste SKU — pode ser a saída
      </span>
    </span>
  );
}

/** Vaga sob o mouse e onde desenhar o cartão de detalhes. */
interface Dica {
  vaga: VagaMapa;
  x: number;
  y: number;
}

/** `null` = ninguém arrastando; 'carga' = levando produto; 'busca' = procurando a saída. */
export type ModoArraste = 'carga' | 'busca' | null;

export interface PropsRackRua {
  centro: number;
  corredor: string | null;
  /** localizador → saldo do SKU escolhido. Marca quem pode ser a SAÍDA. */
  comSku: ReadonlyMap<number, number>;
  origem: number | null;
  destino: number | null;
  /** Posição em destaque especial — o negativo, quando existe um. */
  alerta: { localizador: number; saldoSku: number } | null;
  modoArraste: ModoArraste;
  sobreLoc: number | null;
  ruaAnterior: string | null;
  ruaSeguinte: string | null;
  aoPaginarRua: (direcao: -1 | 1) => void;
  registrarVagas: (m: Map<number, VagaMapa>) => void;
  /** `saldoDoSku` vem preenchido quando a vaga pode ser origem. */
  aoClicarVaga: (vaga: VagaMapa, saldoDoSku: number | null) => void;
  /** Vaga a centralizar ao abrir a rua. */
  focar?: number | null;
  /**
   * A vaga RECOMENDADA para a entrada — livre, no mesmo andar da saída e no
   * módulo mais perto. É atalho, não trava: as outras seguem clicáveis.
   */
  sugerida?: number | null;
  /**
   * O andar da SAÍDA. As vagas nele ficam menos apagadas: mover dentro do
   * mesmo andar não pede empilhadeira, e esse custo tem de aparecer ANTES da
   * escolha — o cartão do topo só o diz depois.
   */
  nivelDaSaida?: number | null;
  dica?: React.ReactNode;
}

export function RackRua({
  centro,
  corredor,
  comSku,
  origem,
  destino,
  alerta,
  modoArraste,
  sobreLoc,
  ruaAnterior,
  ruaSeguinte,
  aoPaginarRua,
  registrarVagas,
  aoClicarVaga,
  focar = null,
  sugerida = null,
  nivelDaSaida = null,
  dica,
}: PropsRackRua): JSX.Element {
  const mapa = useMapa3D(centro, corredor);
  const vagas = mapa.data?.vagas;

  React.useEffect(() => {
    registrarVagas(new Map((vagas ?? []).map((v) => [v.localizador, v])));
  }, [vagas, registrarVagas]);

  const refRolagem = React.useRef<HTMLDivElement | null>(null);
  const refPalco = React.useRef<HTMLDivElement | null>(null);

  /**
   * Cartão de detalhes no hover — os SKUs e as quantidades da vaga.
   *
   * Os dados já vêm no `mapa3d`, então não custa requisição nenhuma. Substitui
   * o `title` nativo: os dois juntos se sobrepunham, e o balão do sistema
   * operacional não cabe uma lista de produtos.
   */
  const [dicaVaga, definirDicaVaga] = React.useState<Dica | null>(null);
  const centralizar = focar ?? origem ?? destino;
  React.useEffect(() => {
    const el = refRolagem.current;
    if (!el || !vagas || vagas.length === 0) return;
    const ordem = [...new Set(vagas.map((v) => v.moduloOrdem))].sort((a, b) => a - b);
    const daVez = vagas.find((v) => v.localizador === centralizar);
    const lote =
      daVez?.moduloOrdem ??
      vagas
        .filter((v) => comSku.has(v.localizador))
        .map((v) => v.moduloOrdem)
        .sort((a, b) => a - b)[0];
    if (lote === undefined) return;
    const i = ordem.indexOf(lote);
    el.scrollLeft = i > 3 ? Math.max(0, i * LARGURA_LOTE - 120) : 0;
  }, [vagas, centralizar, comSku]);

  /**
   * Arrastar até a borda rola a rua; chegando ao fim dela, vira a página para
   * a rua vizinha. É como o operador anda no CD: segue com a carga na mão até
   * o fim do corredor e entra no próximo.
   */
  const mostrarDica = (vaga: VagaMapa, e: React.MouseEvent): void => {
    const el = refPalco.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    definirDicaVaga({ vaga, x: e.clientX - r.left, y: e.clientY - r.top });
  };

  const ladoRef = React.useRef<0 | -1 | 1>(0);
  const [lado, definirLado] = React.useState<0 | -1 | 1>(0);
  const esperaRef = React.useRef(0);
  const emArraste = modoArraste !== null;

  React.useEffect(() => {
    if (emArraste) definirDicaVaga(null);
  }, [emArraste]);

  React.useEffect(() => {
    if (!emArraste) {
      ladoRef.current = 0;
      esperaRef.current = 0;
      definirLado(0);
      return;
    }
    const FAIXA = 64;
    const olhar = (e: PointerEvent): void => {
      const el = refRolagem.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const naAltura = e.clientY >= r.top - 20 && e.clientY <= r.bottom + 20;
      let novo: 0 | -1 | 1 = 0;
      if (naAltura && e.clientX >= r.right - FAIXA) novo = 1;
      else if (naAltura && e.clientX <= r.left + FAIXA) novo = -1;
      if (novo !== ladoRef.current) {
        ladoRef.current = novo;
        esperaRef.current = 0;
        definirLado(novo);
      }
    };
    const passo = (): void => {
      const el = refRolagem.current;
      const dir = ladoRef.current;
      if (!el || dir === 0) return;
      const noFim =
        dir > 0 ? el.scrollLeft + el.clientWidth >= el.scrollWidth - 2 : el.scrollLeft <= 2;
      if (!noFim) {
        el.scrollLeft += dir * 90;
        esperaRef.current = 0;
        return;
      }
      // Fim da rua: segura ~0,8s antes de virar a página, para encostar na
      // borda sem querer não trocar a rua debaixo da mão do operador.
      esperaRef.current += 1;
      if (esperaRef.current >= 11) {
        esperaRef.current = 0;
        aoPaginarRua(dir);
      }
    };
    document.addEventListener('pointermove', olhar);
    const timer = window.setInterval(passo, 70);
    return () => {
      document.removeEventListener('pointermove', olhar);
      window.clearInterval(timer);
    };
  }, [emArraste, aoPaginarRua]);

  if (corredor === null) {
    return (
      <p className="p-8 text-center text-[13px] text-textoFraco">Escolha uma rua para desenhar.</p>
    );
  }
  if (mapa.isLoading) {
    return (
      <div className="p-4">
        <Esqueleto className="h-64" />
      </div>
    );
  }
  if (mapa.isError) return <EstadoErro erro={mapa.error} />;
  const dados: Mapa3DResposta | undefined = mapa.data;
  if (!dados || dados.vagas.length === 0) {
    return <p className="p-8 text-center text-[13px] text-textoFraco">Rua sem vagas desenháveis.</p>;
  }

  const lotes = [...new Set(dados.vagas.map((v) => v.moduloOrdem))].sort((a, b) => a - b);
  const andares = [...new Set(dados.vagas.map((v) => v.nivel))].sort((a, b) => b - a);
  const profs = [...new Set(dados.vagas.map((v) => v.vaga))].sort((a, b) => a - b);
  const grade = new Map(dados.vagas.map((v) => [`${v.moduloOrdem}:${v.nivel}:${v.vaga}`, v]));

  /**
   * BOX real de cada linha do desenho.
   *
   * O rótulo dizia só 'Andar 3', que é o número DERIVADO do BOX — e ninguém
   * na operação fala esse número: fala o BOX ('301'). Mostrando os dois, o
   * desenho passa a casar com o que está na etiqueta e no ERP.
   */
  const boxDaLinha = new Map<string, string>();
  for (const v of dados.vagas) {
    const chave = `${v.nivel}:${v.vaga}`;
    if (!boxDaLinha.has(chave)) {
      const box = lerEndereco(v.endereco).box;
      if (box) boxDaLinha.set(chave, box);
    }
  }

  /**
   * Texto do MÓDULO como está no cadastro ('025'), não o número derivado (25).
   * A régua é a referência que o operador cruza com a etiqueta da rua e com o
   * ERP; zero à esquerda faz parte do código.
   */
  const textoDoModulo = new Map<number, string>();
  for (const v of dados.vagas) {
    if (!textoDoModulo.has(v.moduloOrdem)) {
      const m = lerEndereco(v.endereco).modulo;
      if (m) textoDoModulo.set(v.moduloOrdem, m);
    }
  }

  /** Módulos das duas pontas: a régua os marca para achar de relance. */
  const moduloDe = (loc: number | null): number | null =>
    loc === null ? null : (dados.vagas.find((v) => v.localizador === loc)?.moduloOrdem ?? null);
  const moduloOrigem = moduloDe(origem);
  const moduloDestino = moduloDe(destino);

  return (
    <div className="p-2">
      {/* Dica e legenda na MESMA linha, colados ao desenho. A legenda estava
          na barra de ferramentas, roubando a largura da tira de saídas sem
          estar perto do que explica. */}
      <div className="mb-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
        {dica ? (
          <div className="min-w-[240px] flex-1 text-[11.5px] leading-snug text-textoFraco">
            {dica}
          </div>
        ) : null}
        <LegendaRack />
      </div>

      <div className="relative" ref={refPalco} onMouseLeave={() => definirDicaVaga(null)}>
        {/* Bordas de paginação: só aparecem com algo na mão. */}
        {emArraste && ruaAnterior ? (
          <FaixaDeBorda lado="esquerda" rua={ruaAnterior} ativa={lado === -1} />
        ) : null}
        {emArraste && ruaSeguinte ? (
          <FaixaDeBorda lado="direita" rua={ruaSeguinte} ativa={lado === 1} />
        ) : null}

        <div
          ref={refRolagem}
          className="overflow-auto rounded-xl border border-borda bg-superficie p-2"
        >
          {/* Duas colunas fixas à esquerda: o ANDAR uma vez só, abrangendo as
              linhas dele, e o BOX de cada profundidade ramificando a partir
              dali. Antes 'Andar 6' repetia em 601 e 602, como se fossem dois
              andares. */}
          <div
            className="inline-grid"
            style={{
              gridTemplateColumns: `${LARGURA_ANDAR}px ${LARGURA_BOX}px repeat(${lotes.length}, ${LARGURA_LOTE}px)`,
            }}
          >
            {/* Cabeçalho da régua: diz o que a linha de números É. Sem isso
                eram dígitos soltos, e ninguém adivinha que são módulos. */}
            <div
              className="sticky left-0 z-20 flex h-9 flex-col items-end justify-end bg-superficie pb-[2px] pr-1.5 leading-tight"
              style={{ gridColumn: 'span 2' }}
            >
              <span className="font-mono text-[11px] font-bold text-primaria">RUA {corredor}</span>
              <span className="text-[9px] font-bold uppercase tracking-wide text-textoSuave">
                módulo →
              </span>
            </div>
            {lotes.map((l) => {
              const ehOrigem = l === moduloOrigem;
              const ehDestino = l === moduloDestino;
              return (
                <div
                  key={`h${l}`}
                  className="flex h-9 items-end justify-center pb-[2px]"
                  title={`Módulo ${textoDoModulo.get(l) ?? l} da rua ${corredor}`}
                >
                  <span
                    className={cn(
                      'tabular rounded px-1 font-mono text-[10px] font-bold leading-[14px]',
                      // Discreto: fundo suave e anel, nao bloco solido. A
                      // regua e referencia, nao chamariz.
                      ehOrigem && 'bg-status-livreFundo text-status-livre ring-1 ring-status-livre/50',
                      ehDestino &&
                        !ehOrigem &&
                        'bg-primaria-suave text-primaria ring-1 ring-primaria/40',
                      !ehOrigem && !ehDestino && l % 5 === 0 && 'text-texto',
                      !ehOrigem && !ehDestino && l % 5 !== 0 && 'text-textoSuave',
                    )}
                  >
                    {textoDoModulo.get(l) ?? l}
                  </span>
                </div>
              );
            })}

            {andares.map((n) =>
              profs.map((prof, pi) => (
                <React.Fragment key={`${n}:${prof}`}>
                  {pi === 0 ? (
                    <div
                      className="sticky left-0 z-20 flex items-center justify-end bg-superficie pr-1.5 text-[10.5px] font-semibold text-textoFraco"
                      style={{ gridRow: `span ${profs.length}` }}
                    >
                      {n === 0 ? 'Piso' : `Andar ${n}`}
                    </div>
                  ) : null}
                  <div
                    className="sticky z-20 flex items-center bg-superficie pr-1.5"
                    style={{ height: ALTURA_VAGA, left: LARGURA_ANDAR }}
                  >
                    {profs.length > 1 ? (
                      <Ramo primeiro={pi === 0} ultimo={pi === profs.length - 1} />
                    ) : null}
                    <span className="tabular font-mono text-[10px] font-bold text-textoSuave">
                      {boxDaLinha.get(`${n}:${prof}`) ?? ''}
                    </span>
                  </div>
                  {lotes.map((l) => {
                    const v = grade.get(`${l}:${n}:${prof}`);
                    if (!v) {
                      return (
                        <div
                          key={`${l}:${n}:${prof}`}
                          className="p-[1px]"
                          style={{ height: ALTURA_VAGA }}
                        />
                      );
                    }
                    const saldoSku = comSku.get(v.localizador) ?? null;
                    return (
                      <div
                        key={`${l}:${n}:${prof}`}
                        // `data-loc` é o que o arraste consulta por
                        // `elementFromPoint` — é assim que toda vaga vira
                        // destino sem mil droppables.
                        data-loc={v.localizador}
                        className="p-[1px]"
                        style={{ height: ALTURA_VAGA }}
                      >
                        <Vaga
                          vaga={v}
                          aoEntrar={mostrarDica}
                          aoSair={() => definirDicaVaga(null)}
                          saldoSku={saldoSku}
                          alerta={alerta && alerta.localizador === v.localizador ? alerta : null}
                          ehOrigem={origem === v.localizador}
                          ehDestino={destino === v.localizador}
                          ehSugerida={sugerida === v.localizador}
                          foraDoAndar={nivelDaSaida !== null && v.nivel !== nivelDaSaida}
                          modoArraste={modoArraste}
                          sobMouse={sobreLoc === v.localizador}
                          aoClicar={aoClicarVaga}
                        />
                      </div>
                    );
                  })}
                </React.Fragment>
              )),
            )}
          </div>
        </div>

        {dicaVaga && !emArraste ? (
          <CartaoDaVaga
            dica={dicaVaga}
            largura={refPalco.current?.clientWidth ?? 0}
            altura={refPalco.current?.clientHeight ?? 0}
          />
        ) : null}
      </div>
    </div>
  );
}

/**
 * O que tem dentro da vaga: SKUs, descrições e quantidades.
 *
 * Mesmo cartão do mapa 3D (`Rack3D`), de propósito — o operador já aprendeu a
 * lê-lo ali. Vira para o lado e para cima quando esbarraria na borda.
 */
function CartaoDaVaga({
  dica,
  largura,
  altura,
}: {
  dica: Dica;
  largura: number;
  altura: number;
}): JSX.Element {
  const { vaga } = dica;
  const pc = vaga.pct == null ? 'sem medida' : `${vaga.pct}%`;
  const cor = SEMCOR[vaga.semaforo] ?? SEMCOR.sem_medida;
  const inverter = dica.x > largura - 280;
  const subir = altura > 0 && dica.y > altura * 0.5;
  return (
    <div
      role="status"
      className="pointer-events-none absolute z-50 w-[264px] overflow-hidden rounded-xl text-xs leading-relaxed text-white shadow-forte ring-1 ring-white/10"
      style={{
        background: 'linear-gradient(180deg, #1e2436 0%, #141824 100%)',
        left: inverter ? undefined : dica.x + 16,
        right: inverter ? largura - dica.x + 16 : undefined,
        top: subir ? undefined : Math.max(8, dica.y + 16),
        bottom: subir ? Math.max(8, altura - dica.y + 16) : undefined,
        maxHeight: altura > 0 ? Math.max(140, altura - 16) : undefined,
      }}
    >
      <div className="flex items-center gap-2 px-3 pt-2.5">
        <span className="font-mono text-[13px] font-bold tracking-wide text-indigo-200">
          {comoEndereco(vaga.endereco)}
        </span>
        <span
          className="ml-auto rounded-md px-1.5 py-0.5 text-[11px] font-bold"
          style={{ background: cor, color: vaga.semaforo === 'medio' ? '#3f2d00' : '#fff' }}
        >
          {pc}
        </span>
      </div>
      <p className="flex flex-wrap items-center gap-x-1.5 px-3 pb-1.5 pt-0.5 text-[10.5px] text-white/45">
        <span className="rounded bg-white/10 px-1 py-px font-mono text-[10px] font-bold text-white/75">
          LOC {vaga.localizador}
        </span>
        <span>
          Lote {vaga.moduloOrdem} · {vaga.nivel === 0 ? 'Piso' : `Andar ${vaga.nivel}`} · BOX{' '}
          {lerEndereco(vaga.endereco).box}
        </span>
      </p>
      {vaga.saldo === 0 ? (
        <p className="border-t border-white/10 px-3 py-2 text-white/60">
          Vaga livre — pronta para receber.
        </p>
      ) : (
        <div className="max-h-[220px] overflow-y-auto">
          {vaga.produtos.map((p) => (
            <div key={p.sku} className="flex items-center gap-2 border-t border-white/10 px-3 py-2">
              <img
                src={urlImagemProduto(p.sku)}
                alt=""
                loading="lazy"
                width={40}
                height={40}
                className="h-10 w-10 shrink-0 rounded-md bg-white object-contain"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.visibility = 'hidden';
                }}
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-[12px] font-medium text-white">{p.descricao}</p>
                <p className="text-[10.5px] text-white/55">
                  SKU {p.sku}
                  {p.ean ? ` · EAN ${p.ean}` : ''}
                </p>
              </div>
              <div className="shrink-0 text-right">
                <b className="tabular text-[14px] text-white">{numero(p.quantidade)}</b>
                <span className="block text-[10px] text-white/55">un</span>
              </div>
            </div>
          ))}
        </div>
      )}
      {vaga.saldo < 0 ? (
        <p className="border-t border-white/10 bg-red-500/10 px-3 py-1.5 font-semibold text-red-300">
          saldo negativo ({numero(vaga.saldo)})
        </p>
      ) : null}
    </div>
  );
}


/** Zona de virada de rua, nas laterais do desenho. */
function FaixaDeBorda({
  lado,
  rua,
  ativa,
}: {
  lado: 'esquerda' | 'direita';
  rua: string;
  ativa: boolean;
}): JSX.Element {
  const Icone = lado === 'esquerda' ? ChevronLeft : ChevronRight;
  return (
    <div
      aria-hidden
      className={cn(
        'pointer-events-none absolute inset-y-0 z-40 flex w-16 flex-col items-center justify-center gap-1 rounded-xl transition-all',
        lado === 'esquerda' ? 'left-0' : 'right-0',
        ativa
          ? 'bg-primaria/85 text-white shadow-forte'
          : 'bg-primaria-suave/85 text-primaria ring-1 ring-inset ring-primaria/30',
      )}
    >
      <Icone className={cn('h-5 w-5', ativa && 'animate-pulsoSuave')} />
      <span className="px-1 text-center text-[10px] font-bold leading-tight">{rua}</span>
      {ativa ? <span className="text-[9px] font-semibold">segure aqui</span> : null}
    </div>
  );
}

/**
 * Uma vaga do desenho.
 *
 * Arrastável só quando tem saldo do SKU — é o que pode ser a saída, por regra
 * do PBS, que recusa saída sem saldo. Clicável sempre: clique em quem tem
 * saldo escolhe a origem; clique em qualquer outra escolhe o destino. O
 * arraste e o clique fazem a mesma coisa, de propósito.
 */
function Vaga({
  vaga,
  aoEntrar,
  aoSair,
  saldoSku,
  alerta,
  ehOrigem,
  ehDestino,
  ehSugerida,
  foraDoAndar,
  modoArraste,
  sobMouse,
  aoClicar,
}: {
  vaga: VagaMapa;
  aoEntrar: (vaga: VagaMapa, e: React.MouseEvent) => void;
  aoSair: () => void;
  saldoSku: number | null;
  alerta: { localizador: number; saldoSku: number } | null;
  ehOrigem: boolean;
  ehDestino: boolean;
  ehSugerida: boolean;
  /** Fora do andar da saída — recua, porque mover para lá custa equipamento. */
  foraDoAndar: boolean;
  modoArraste: ModoArraste;
  sobMouse: boolean;
  aoClicar: (vaga: VagaMapa, saldoDoSku: number | null) => void;
}): JSX.Element {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: idVaga(vaga.localizador),
    disabled: saldoSku === null,
  });

  // Vaga sem saldo: `pct` vem 0, não null — dizer "0% ocupada" esconde
  // justamente o que o operador procura, que é vaga vazia.
  const ocupacao =
    vaga.saldo === 0
      ? 'VAGA LIVRE'
      : vaga.pct !== null
        ? `${vaga.pct}% ocupada`
        : 'sem medida cadastrada';

  // `aria-label`, não `title`: o balão nativo do sistema aparecia POR CIMA do
  // cartão de detalhes e um tapava o outro.
  const titulo = [
    comoEndereco(vaga.endereco),
    `LOC ${vaga.localizador}`,
    alerta ? `NEGATIVO · ${numero(alerta.saldoSku)} un deste SKU` : ocupacao,
    saldoSku !== null
      ? `saldo de ${numero(saldoSku)} un deste SKU — pode ser saída ou entrada`
      : 'pode receber a entrada',
    ehSugerida ? 'SUGERIDA — livre, no mesmo andar da saída' : null,
  ]
    .filter(Boolean)
    .join(' · ');

  // Levando carga, toda vaga recebe. Procurando a saída, só quem tem saldo.
  const podeReceber =
    modoArraste === 'carga' ? !isDragging : modoArraste === 'busca' ? saldoSku !== null : false;
  const realceSolte = sobMouse && podeReceber;
  const emArraste = modoArraste !== null;

  return (
    <button
      ref={setNodeRef}
      type="button"
      {...attributes}
      {...listeners}
      onClick={() => aoClicar(vaga, saldoSku)}
      onMouseEnter={(e) => aoEntrar(vaga, e)}
      onMouseMove={(e) => aoEntrar(vaga, e)}
      onMouseLeave={aoSair}
      aria-label={titulo}
      data-titulo={titulo}
      className={cn(
        'tabular grid h-full w-full place-items-center rounded-[3px] text-[9px] font-bold transition-transform',
        saldoSku !== null ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer',
        vaga.saldo === 0 && !alerta ? 'text-textoFraco' : 'text-white',
        // `ring-inset`, não `outline`: o tailwind-merge descartava a classe
        // `outline` por conflito com `outline-primaria`, o outline-style ficava
        // `none` e a marca que a tela manda procurar nunca era desenhada.
        saldoSku !== null && !ehOrigem && 'ring-2 ring-inset ring-primaria',
        ehOrigem && 'z-20 scale-125 ring-2 ring-status-livre ring-offset-1',
        alerta && !realceSolte && 'animate-pulsoSuave ring-2 ring-status-divergencia ring-offset-1',
        ehDestino && !alerta && !realceSolte && 'z-20 ring-2 ring-primaria ring-offset-1',
        /* A sugerida pisca discreto até alguém decidir. Some assim que o
           destino é escolhido — sugestão cumprida não fica piscando. */
        ehSugerida &&
          !ehDestino &&
          !alerta &&
          !realceSolte &&
          'z-10 animate-pulsoSuave ring-2 ring-status-livre ring-offset-1',
        emArraste && alerta && !realceSolte && 'z-20 scale-[1.45] ring-4',
        modoArraste === 'busca' && saldoSku === null && !alerta && 'opacity-25',
        /* Fora do andar da saída a vaga recua: continua clicável, mas deixa de
           competir por atenção com as que não custam empilhadeira. */
        foraDoAndar && !ehOrigem && !ehDestino && !alerta && 'opacity-60',
        realceSolte && 'z-30 scale-[1.7] bg-status-livre text-white ring-4 ring-status-livre',
        isDragging && 'opacity-30',
      )}
      style={
        realceSolte
          ? undefined
          : {
              // Vaga livre pintada de azul, como no mapa: e o destino
              // provavel de quem esta arrastando, entao tem de saltar em vez
              // de sumir no fundo.
              background: alerta
                ? SEMCOR.divergencia
                : ehOrigem
                  ? 'rgb(var(--st-livre))'
                  : vaga.saldo === 0
                    ? SEMCOR.vazio
                    : (SEMCOR[vaga.semaforo] ?? SEMCOR.sem_medida),
              boxShadow: undefined,
            }
      }
    >
      {realceSolte ? (
        <Check className="h-3.5 w-3.5" />
      ) : alerta ? (
        numero(alerta.saldoSku)
      ) : saldoSku !== null ? (
        saldoSku
      ) : (
        ''
      )}
    </button>
  );
}

/**
 * Rastreia qual vaga está sob o cursor durante um arraste.
 *
 * Devolve o localizador para desenhar o realce e um `ref` com o mesmo valor,
 * porque o handler de `onDragEnd` fecha sobre o estado antigo.
 */
export function useVagaSobCursor(ativo: boolean): {
  sobreLoc: number | null;
  sobreRef: React.MutableRefObject<number | null>;
} {
  const [sobreLoc, definirSobreLoc] = React.useState<number | null>(null);
  const sobreRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    if (!ativo) {
      sobreRef.current = null;
      definirSobreLoc(null);
      return;
    }
    const mover = (e: PointerEvent): void => {
      const alvo = document.elementFromPoint(e.clientX, e.clientY);
      const celula = alvo?.closest('[data-loc]');
      const loc = celula ? Number(celula.getAttribute('data-loc')) : null;
      const valido = loc !== null && Number.isFinite(loc) ? loc : null;
      sobreRef.current = valido;
      definirSobreLoc(valido);
    };
    document.addEventListener('pointermove', mover);
    return () => document.removeEventListener('pointermove', mover);
  }, [ativo]);

  return { sobreLoc, sobreRef };
}
